import { useCallback, useEffect, useState } from 'react'
import { fetchWalletBalances } from '../../api/wallet'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import type { WalletBalance } from '../../types/wallet'
import TokenIcon from '../common/TokenIcon'
import JupiterSwapPanel from './JupiterSwapPanel'

function usd(v: string) {
  return '$' + parseFloat(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

export default function WalletPanel({ onPanelUsd, wsData }: { onPanelUsd?: (key: 'wallet', usd: number) => void; wsData?: WalletBalance[] | null } = {}) {
  const { portfolioId } = useAuth()
  const [swapOpen, setSwapOpen] = useState(false)
  const [balances, setBalances] = useState<WalletBalance[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const shimmerRef = useShimmerOnUpdate(wsData)

  // Initial data: use batch snapshot from parent when available, otherwise fall back to
  // individual REST fetch. wsData is intentionally excluded from deps — it is only sampled
  // at mount / portfolioId change to decide whether a fetch is necessary.
  useEffect(() => {
    if (!portfolioId) return
    if (wsData != null) { setLoading(false); return }
    setLoading(true)
    fetchWalletBalances(portfolioId)
      .then(data => { setBalances(data); setError(null) })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [portfolioId])  

  // Live update from WebSocket push
  useEffect(() => {
    if (wsData == null) return
    setBalances(wsData)
    setLoading(false)
  }, [wsData])

  const refreshBalances = useCallback(() => {
    if (!portfolioId) return
    fetchWalletBalances(portfolioId)
      .then(data => { setBalances(data); setError(null) })
      .catch(e => setError(String(e)))
  }, [portfolioId])

  useEffect(() => {
    if (balances) onPanelUsd?.('wallet', balances.reduce((s, b) => s + parseFloat(b.usdValue), 0))
  }, [balances])

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-fringe shrink-0">
        <div className="w-7 h-7 rounded-lg bg-accent-purple/10 flex items-center justify-center text-accent-purple">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
            <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2"/>
            <path d="M16 12a2 2 0 0 0 0 4h5v-4h-5z"/>
          </svg>
        </div>
        <span className="text-sm font-semibold text-ink">Wallet</span>
        {balances && (
          <span className="ml-1 text-xs bg-accent-blue/20 text-accent-blue px-1.5 py-0.5 rounded-full font-medium">
            {balances.length}
          </span>
        )}
        <button
          onClick={() => setSwapOpen(o => !o)}
          className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-accent-purple/10 text-accent-purple hover:bg-accent-purple/20 transition-colors font-medium"
        >
          ⇄ Swap
        </button>
        {balances && balances.length > 0 && !loading && (
          <span className="text-sm font-semibold text-ink">
            {usd(String(balances.reduce((s, b) => s + parseFloat(b.usdValue), 0)))}
          </span>
        )}
      </div>

      {/* Body */}
      {error && (
        <p className="text-accent-red text-xs p-4">{error}</p>
      )}
      {loading && (
        <div className="animate-pulse p-4 space-y-2">
          <div className="h-3 w-full bg-surface rounded"/>
          <div className="h-3 w-3/4 bg-surface rounded"/>
          <div className="h-3 w-5/6 bg-surface rounded"/>
        </div>
      )}
      {!loading && balances && balances.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-8">No wallet balances found</p>
      )}
      {!loading && balances && balances.length > 0 && (
        <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-surface flex-1">
            <div className="min-w-[400px]">
              {/* Column headers */}
              <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr] gap-x-3 px-4 sm:px-5 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-fringe">
                <span>Asset</span>
                <span className="text-right">Balance</span>
                <span className="text-right">Price</span>
                <span className="text-right">Value</span>
              </div>

              <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-surface scrollbar-track-transparent divide-y divide-white/5 max-h-[320px] lg:max-h-none">
                {balances.map((b, i) => {
                  const displaySymbol = b.symbol.length >= 32
                    ? `\u2026${b.symbol.slice(-8)}`
                    : b.symbol
                  return (
                  <div key={b.symbol + i} className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr] gap-x-3 px-4 sm:px-5 py-2.5 sm:py-3 items-center hover:bg-surface-hover/30 transition-colors text-sm">
                    <span className="font-medium text-ink flex items-center gap-2">
                      <TokenIcon symbol={b.symbol} mint={b.mintAddress} size={20} />
                      {displaySymbol}
                    </span>
                    <span className="text-right text-ink-soft">{parseFloat(b.amount).toFixed(4)}</span>
                    <span className="text-right text-accent-blue">{usd(b.priceUsd)}</span>
                    <span className="text-right text-ink font-semibold">{usd(b.usdValue)}</span>
                  </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Swap slide panel — native SwapForm signed via wallet adapter */}
      {swapOpen && (
        <JupiterSwapPanel onClose={() => setSwapOpen(false)} onSwapSuccess={refreshBalances} />
      )}
    </div>
  )
}
